import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from '../shared/store/hooks';
import { selectCartCount } from '../shared/store/cartSlice';
import { selectWishlistCount } from '../shared/store/wishlistSlice';
import '../../src/layouts/styles/navbar.css';

const NAV_LINKS = [
    { label: "Home", path: "/" },
    { label: "Catalogue", path: "/products" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
];

const PRODUCTS = [
    { label: "Pom Poms", path: "/products/pom-poms" },
    { label: "Faux Fur Pom Poms", path: "/faux-fur-pom-poms" },
    { label: "Tassels", path: "/products/tassels" },
    { label: "Fringes", path: "/products/fringes" },
    { label: "Beaded Patches", path: "/products/beaded-patches" },
];

const NavBar = () => {
    const navigate = useNavigate();
    const cartCount = useAppSelector(selectCartCount);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showAnnounce, setShowAnnounce] = useState(true);
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const go = (path: string) => {
        navigate(path);
        setMenuOpen(false);
    };

    const handleSearch = (e: React.SyntheticEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchOpen(false);
            setSearchQuery("");
        }
    };

    return (
        <header className="nb-header">

            {/* ── Announcement bar ── */}
            {showAnnounce && (
                <div className="nb-announce">
                    <div className="nb-announce-inner">
                        <span>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4
                            .5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                            Mobile No: +91-9818548369 | Email:info@artecraftindia.com
                        </span>
                        <span className="nb-announce-sep">|</span>
                        <span>Est. 2017 — Trusted handmade products crafted using traditional skills Manufacturer, New Delhi</span>
                    </div>
                    <button className="nb-announce-close" onClick={() => setShowAnnounce(false)} aria-label="Dismiss">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            )}

            {/* ── Search overlay ── */}
            {searchOpen && (
                <div className="nb-search-overlay" onClick={() => setSearchOpen(false)}>
                    <div className="nb-search-box" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleSearch}>
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search products — pom poms, tassels, fringes…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="nb-search-input"
                            />
                            <button type="submit" className="nb-search-submit" aria-label="Search">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                            </button>
                        </form>
                        <button className="nb-search-close" onClick={() => setSearchOpen(false)} aria-label="Close search">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* ── Main nav ── */}
            <nav className={`nb-nav${scrolled ? " nb-nav--shadow" : ""}`}>
                <div className="nb-container">

                    {/* Logo */}
                    <button className="nb-logo" onClick={() => go("/")} aria-label="Arte Craft India home">
                        <img
                            src={require('../assets/img/logo/artecraftindia.jpg')}
                            alt="Arte Craft India"
                            className="nb-logo-img"
                        />
                        <div className="nb-logo-text">
                            <span className="nb-logo-brand">Arte Craft</span>
                            <span className="nb-logo-country">India</span>
                        </div>
                    </button>

                    {/* Desktop nav links */}
                    <ul className="nb-links" role="menubar">
                        {NAV_LINKS.map(link => (
                            <li key={link.path} className="nb-item" role="none">
                                <button role="menuitem" onClick={() => go(link.path)} className="nb-link">
                                    {link.label}
                                </button>
                            </li>
                        ))}

                        {/* Products dropdown */}
                        <li className="nb-item nb-has-drop" role="none">
                            <button role="menuitem" className="nb-link nb-link--drop">
                                Products
                                <svg className="nb-chevron" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </button>
                            <div className="nb-dropdown" role="menu">
                                <div className="nb-drop-header">Our Products</div>
                                {PRODUCTS.map(p => (
                                    <button key={p.path} role="menuitem" className="nb-drop-item" onClick={() => go(p.path)}>
                                        <svg width="5" height="5" viewBox="0 0 8 8" fill="currentColor">
                                            <circle cx="4" cy="4" r="4"/>
                                        </svg>
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </li>
                    </ul>

                    {/* Right action icons */}
                    <div className="nb-actions">
                        <button className="nb-icon-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                        </button>

                        <button className="nb-icon-btn" onClick={() => go("/wishlist")} aria-label="Wishlist">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                        </button>

                        <button className="nb-icon-btn nb-cart-btn" onClick={() => go("/cart")} aria-label="Cart">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                            </svg>
                            {cartCount > 0 && <span className="nb-badge">{cartCount}</span>}
                        </button>

                        <button className="nb-icon-btn" onClick={() => go("/login")} aria-label="Account">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                        </button>

                        {/* Quote CTA — desktop only */}
                        <button className="nb-quote-btn" onClick={() => go("/request-a-quote")}>
                            Request a Quote
                        </button>
                    </div>

                    {/* Hamburger — mobile only */}
                    <button
                        className={`nb-burger${menuOpen ? " nb-burger--open" : ""}`}
                        onClick={() => setMenuOpen(v => !v)}
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </nav>

            {/* ── Mobile drawer ── */}
            <div className={`nb-drawer${menuOpen ? " nb-drawer--open" : ""}`} aria-hidden={!menuOpen}>
                <div className="nb-drawer-head">
                    <button className="nb-logo nb-logo--sm" onClick={() => go("/")}>
                        <img src={require('../assets/img/logo/artecraftindia.jpg')} alt="Arte Craft India" className="nb-logo-img" />
                        <div className="nb-logo-text">
                            <span className="nb-logo-brand">Arte Craft</span>
                            <span className="nb-logo-country">India</span>
                        </div>
                    </button>
                    <button className="nb-drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div className="nb-drawer-body">
                    {NAV_LINKS.map(link => (
                        <button key={link.path} className="nb-drawer-link" onClick={() => go(link.path)}>
                            {link.label}
                        </button>
                    ))}

                    <div className="nb-drawer-section">Products</div>
                    {PRODUCTS.map(p => (
                        <button key={p.path} className="nb-drawer-link nb-drawer-link--sub" onClick={() => go(p.path)}>
                            <svg width="5" height="5" viewBox="0 0 8 8" fill="currentColor">
                                <circle cx="4" cy="4" r="4"/>
                            </svg>
                            {p.label}
                        </button>
                    ))}

                    <div className="nb-drawer-actions">
                        <button className="nb-drawer-action-btn" onClick={() => { setSearchOpen(true); setMenuOpen(false); }}>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            Search
                        </button>
                        <button className="nb-drawer-action-btn" onClick={() => go("/cart")}>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                            </svg>
                            Cart
                        </button>
                        <button className="nb-drawer-action-btn" onClick={() => go("/login")}>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                            Account
                        </button>
                    </div>

                    <button className="nb-quote-btn nb-quote-btn--full" onClick={() => go("/request-a-quote")}>
                        Request a Quote
                    </button>
                </div>
            </div>

            {/* Backdrop */}
            {menuOpen && <div className="nb-overlay" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

        </header>
    );
};

export default NavBar;
